/**
 * UserAccount — the auth/routing-relevant slice of aligned's `user_accounts`
 * collection (109 fields total; full model is ported per-feature). Docs are keyed
 * by an auto-generated id (mirrored in `id`); the Firebase Auth uid lives in
 * `firebaseId`. Roles: `isAdmin`/`isOperationsManager` are fields here (source of
 * truth for the guards); `isContractorAdmin` is mirrored for admin search display
 * but authorization uses its Firebase custom claim; `isManager` is a custom claim
 * only (computed from the `managers` collection), so it is NOT on this doc.
 */
/**
 * Where an expert stands in identity verification.
 *
 * Persona runs this in TWO independent stages, and the distinction is the whole
 * reason this type exists:
 *
 *   1. the ID/selfie check itself, which the expert can fix and retry; and
 *   2. a decisioning Workflow (Tor/VPN, selfie liveness, behaviour threat,
 *      watchlist, PEP) that runs afterwards and takes seconds to return.
 *
 * Persona reports `completed` at the end of stage one. That is NOT a pass — the
 * screening has not run yet. The two booleans this replaces
 * (`hasCompletedIdentityVerification`, `isVerifiedIdentity`) had no way to say
 * "passed the ID check, waiting on the Workflow", so that state kept being
 * collapsed into "verified" and letting people through unscreened.
 *
 * `approved` is the only value that means verified.
 */
export type IdVerificationStatus =
  /** No inquiry has been started. */
  | 'notStarted'
  /** Stage one failed — bad photo, wrong document, VPN. Theirs to fix; retry. */
  | 'idCheckFailed'
  /** Stage one passed; the Workflow has not returned a decision yet. */
  | 'awaitingDecision'
  /** Stage two passed. The ONLY verified state. */
  | 'approved'
  /** Stage two refused. Blocked; not retryable — a retry cannot change a hit. */
  | 'declined'
  /** Stage two held for review. Blocked; not retryable. */
  | 'needsReview'
  /**
   * Stage one passed but no decision ever arrived — our pipeline failed, not
   * theirs. A support ticket is raised and they are NEVER asked to re-verify:
   * the ID check already passed, so sending them back discards work they did.
   */
  | 'stuck';

const ID_VERIFICATION_STATUSES: readonly IdVerificationStatus[] = [
  'notStarted',
  'idCheckFailed',
  'awaitingDecision',
  'approved',
  'declined',
  'needsReview',
  'stuck',
];

/**
 * Unknown values become `notStarted` rather than throwing, matching
 * `parsePersonaInquiryStatus`. An unreadable status must never read as verified.
 */
export function parseIdVerificationStatus(value: unknown): IdVerificationStatus {
  return typeof value === 'string' && (ID_VERIFICATION_STATUSES as readonly string[]).includes(value)
    ? (value as IdVerificationStatus)
    : 'notStarted';
}

/** The single definition of "verified". Nothing else grants access. */
export function isIdentityVerified(status: IdVerificationStatus): boolean {
  return status === 'approved';
}

/**
 * Resolves an expert's status, falling back to the legacy booleans.
 *
 * Accounts written before `idVerificationStatus` existed carry their outcome in
 * two booleans across two documents, so both are needed to read them:
 *
 *   - `isVerifiedIdentity` (private/info) true  -> they passed -> `approved`
 *   - `hasCompletedIdentityVerification` true, `isVerifiedIdentity` false
 *     -> the step settled without passing. The old flags could not say whether
 *        the ID check failed or the decision never came, so this reports
 *        `idCheckFailed` — the retryable reading. Being wrong towards
 *        "try again" is better than wrongly reporting verified.
 *   - neither -> `notStarted`
 *
 * Pass `isVerifiedIdentity: undefined` when private/info has not been loaded;
 * an unknown legacy account then reads as unverified rather than verified.
 */
export function effectiveIdVerificationStatus(
  account: Pick<UserAccount, 'idVerificationStatus' | 'hasCompletedIdentityVerification'>,
  isVerifiedIdentity?: boolean,
): IdVerificationStatus {
  if (account.idVerificationStatus) return account.idVerificationStatus;
  if (isVerifiedIdentity) return 'approved';
  return account.hasCompletedIdentityVerification ? 'idCheckFailed' : 'notStarted';
}

/**
 * The booleans to write alongside a status, so the admin toggles and the drift
 * scripts keep reading the truth. They are projections — never the source.
 */
export function idVerificationFlagsFor(status: IdVerificationStatus): {
  hasCompletedIdentityVerification: boolean;
  isVerifiedIdentity: boolean;
} {
  return {
    hasCompletedIdentityVerification: status !== 'notStarted',
    isVerifiedIdentity: isIdentityVerified(status),
  };
}

/**
 * True when the expert can usefully try again — stage-one problems only.
 *
 * A stage-two decline is deliberately excluded: retrying cannot change a
 * watchlist hit, and `stuck` means they already passed, so re-running the ID
 * check would throw away work they had done.
 */
export function canRetryIdVerification(status: IdVerificationStatus): boolean {
  return status === 'notStarted' || status === 'idCheckFailed';
}

/** True when the expert is held and only support can move them forward. */
export function isIdVerificationBlocked(status: IdVerificationStatus): boolean {
  return status === 'declined' || status === 'needsReview' || status === 'stuck';
}

export interface UserAccount {
  id: string;
  firebaseId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  firstNamePreferred?: string;
  /** Firebase Storage URL for an uploaded profile photo — only trusted for the avatar when it's a `firebasestorage.googleapis.com`/`profile_images` URL, matching aligned's `_profileImageUrl` cache check. */
  profilePictureUrl?: string;
  isOwner?: boolean;
  isFinanceAdmin?: boolean;
  isAdmin: boolean;
  isOperationsManager: boolean;
  /** Where they came from, set by the acquisition match (detail in `acquisitions/{id}`). Null until matched. */
  acquisitionSource?: string | null;
  /** Mirrored custom claim used by the full-admin permission editor. */
  isContractorAdmin?: boolean;
  isVerifiedEmail: boolean;
  /** When SendGrid last accepted an email-verification code for this user; set
   *  server-side by `submitEmailVerification`. Absent means no code was ever
   *  sent — which `isVerifiedEmail` cannot tell you, since it defaults to true. */
  sentVerificationAt?: string | null;
  /** When the user entered a correct code. Cleared whenever a fresh code is
   *  issued, so it never contradicts `isVerifiedEmail`. */
  verifiedAt?: string | null;
  isSubscribedEmail?: boolean;
  raterOnboardingComplete: boolean;
  managerOnboardingComplete: boolean;
  status: string;
  createdAt?: string | null;
  lastSignInDate?: string | null;
  accountDisqualificationReason?: string | null;
  reviewPriority?: string | null;
  reviewEvidenceRequested?: boolean;
  suspendedOn?: string | null;
  suspendedUntil?: string | null;
  suspendedBy?: string | null;
  currentCustomerId: string | null;
  /** Pointer to the current pending expert_earnings accumulator, when one exists. */
  currentPendingExpertEarningsId?: string | null;
  /** Last app area the user visited ('EXPERT'/'MANAGER'/...), stamped on every manager-area visit. */
  lastAppType: string | null;
  messageBoardUserName?: string;
  /** Expert pools this expert is approved for; gates pool-restricted tasks. */
  assignedExpertPoolIds: string[];
  /** 0-5, rolled up server-side; null until the expert has reviewed work. */
  starRating: number | null;
  /** Micro-USD; the expert's current bid, editable from Settings/Payments. */
  currentHourlyWageBid: number;
  hasAutoExpertPoolAssignments: boolean;
  /** Set true whenever the expert has a non-empty LinkedIn URL on file. */
  isVerifiedLinkedIn: boolean;
  /** Onboarding wizard: account step (name/address/DOB/gender) completed. */
  hasCompletedAccountInfo: boolean;
  /**
   * Onboarding wizard: the Persona identity step was reached and settled.
   *
   * Derived from `idVerificationStatus` (`!== 'notStarted'`) and kept only so
   * existing admin toggles and the drift scripts keep working. It says the step
   * happened, NOT that the expert passed — read `idVerificationStatus` for that.
   */
  hasCompletedIdentityVerification: boolean;
  /**
   * Where the expert stands in identity verification. Source of truth; the two
   * booleans are projections of it.
   *
   * Optional because accounts predating this field have no value and the truth
   * for them is split across two documents. Resolve it with
   * `effectiveIdVerificationStatus(account, privateInfo)` rather than reading
   * this directly.
   */
  idVerificationStatus?: IdVerificationStatus;
  canSeeMessageBoard?: boolean;
  expertTierEducation?: string | null;
  expertTierProfessional?: string | null;
  expertTierAligned?: string | null;
  expertTierJustification?: string | null;
  summaryEducation?: string | null;
  summaryProfessionalExperience?: string | null;
}

type Data = Record<string, unknown>;

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}
function bool(v: unknown): boolean {
  return v === true;
}
function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}
function num(v: unknown, dflt: number): number {
  return typeof v === 'number' ? v : dflt;
}
function isoString(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate: unknown }).toDate === 'function') {
    const date = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return null;
}

export function mapUserAccount(id: string, data: Data): UserAccount {
  return {
    id,
    firebaseId: str(data.firebaseId) ?? '',
    email: str(data.email) ?? '',
    firstName: str(data.firstName),
    lastName: str(data.lastName),
    firstNamePreferred: str(data.firstNamePreferred),
    profilePictureUrl: str(data.profilePictureUrl),
    isOwner: bool(data.isOwner),
    isFinanceAdmin: bool(data.isFinanceAdmin),
    isAdmin: bool(data.isAdmin),
    // Absent on docs where false — defaults to false.
    isOperationsManager: bool(data.isOperationsManager),
    acquisitionSource: typeof data.acquisitionSource === 'string' ? data.acquisitionSource : null,
    isContractorAdmin: bool(data.isContractorAdmin),
    // Default true in the source model; only false once email verification is triggered.
    isVerifiedEmail: data.isVerifiedEmail !== false,
    sentVerificationAt: isoString(data.sentVerificationAt),
    verifiedAt: isoString(data.verifiedAt),
    // Default true in aligned unless explicitly unsubscribed.
    isSubscribedEmail: data.isSubscribedEmail !== false,
    raterOnboardingComplete: bool(data.raterOnboardingComplete),
    managerOnboardingComplete: bool(data.managerOnboardingComplete),
    status: str(data.status) ?? 'active',
    createdAt: isoString(data.createdAt),
    lastSignInDate: isoString(data.lastSignInDate),
    accountDisqualificationReason: str(data.accountDisqualificationReason) ?? null,
    reviewPriority: str(data.reviewPriority) ?? null,
    reviewEvidenceRequested: bool(data.reviewEvidenceRequested),
    suspendedOn: isoString(data.suspendedOn),
    suspendedUntil: isoString(data.suspendedUntil),
    suspendedBy: str(data.suspendedBy) ?? null,
    currentCustomerId: str(data.currentCustomerId) ?? null,
    currentPendingExpertEarningsId: str(data.currentPendingExpertEarningsId) ?? null,
    lastAppType: str(data.lastAppType) ?? null,
    messageBoardUserName: str(data.messageBoardUserName),
    assignedExpertPoolIds: strList(data.assignedExpertPoolIds),
    starRating: numOrNull(data.starRating),
    currentHourlyWageBid: num(data.currentHourlyWageBid, 0),
    hasAutoExpertPoolAssignments: bool(data.hasAutoExpertPoolAssignments),
    isVerifiedLinkedIn: bool(data.isVerifiedLinkedIn),
    hasCompletedAccountInfo: bool(data.hasCompletedAccountInfo),
    hasCompletedIdentityVerification: bool(data.hasCompletedIdentityVerification),
    // Absent on every account written before this field existed, and left absent
    // rather than guessed. The truth lives across two documents — this one and
    // private/info.isVerifiedIdentity — and the mapper only sees this one, so it
    // cannot tell a legacy pass from a legacy failure. Callers fall back to the
    // booleans for those accounts (see `effectiveIdVerificationStatus`); writing
    // a guess here would either un-verify people who passed or verify people who
    // did not.
    idVerificationStatus: data.idVerificationStatus === undefined
      ? undefined
      : parseIdVerificationStatus(data.idVerificationStatus),
    canSeeMessageBoard: bool(data.canSeeMessageBoard),
    expertTierEducation: str(data.expertTierEducation) ?? null,
    expertTierProfessional: str(data.expertTierProfessional) ?? null,
    expertTierAligned: str(data.expertTierAligned) ?? null,
    expertTierJustification: str(data.expertTierJustification) ?? null,
    summaryEducation: str(data.summaryEducation) ?? null,
    summaryProfessionalExperience: str(data.summaryProfessionalExperience) ?? null,
  };
}
